import { useCallback } from "react";

import { BoundsPreviewMap } from "@/features/widget-forms/forms/weather-model-map/components/bounds-preview-map";
import CheckCard, {
  StatusLabel,
} from "@/features/widget-forms/forms/weather-model-map/components/check-card/check-card";
import { CoordinateField } from "@/features/widget-forms/forms/map-widget-editor/coordinate-field";
import {
  initialViewportPresets,
  viewportBoundsEqual,
} from "@/features/widget-forms/shared/initial-viewport-presets";
import type { ParametersGeoBoundingBox } from "@/lib/api";
import panelStyles from "@/features/widget-forms/forms/weather-model-map/components/initial-viewport-tab/initial-viewport-tab.module.css";

const PRESET_KEYS = ["nl", "bes"] as const;

export type InitialViewportPanelStrings = {
  title: string;
  subtitle: string;
  manualTitle: string;
  northLatitude: string;
  westLongitude: string;
  eastLongitude: string;
  southLatitude: string;
  presetsTitle: string;
  presetNl: string;
  presetNlDescription: string;
  presetBes: string;
  presetBesDescription: string;
  selectedStatusText: string;
};

type InitialViewportPanelProps = {
  bounds: ParametersGeoBoundingBox;
  baseLayerStyle: string;
  onBoundsChange: (bounds: ParametersGeoBoundingBox) => void;
  onCoordChange: (
    field: keyof Omit<ParametersGeoBoundingBox, "type">,
    rawValue: unknown,
  ) => void;
  strings: InitialViewportPanelStrings;
};

export function InitialViewportPanel({
  bounds,
  baseLayerStyle,
  onBoundsChange,
  onCoordChange,
  strings,
}: InitialViewportPanelProps) {
  const selectedPreset = PRESET_KEYS.find((key) =>
    viewportBoundsEqual(initialViewportPresets[key], bounds),
  );

  const handlePresetSelect = useCallback(
    (key: (typeof PRESET_KEYS)[number]) => {
      onBoundsChange(initialViewportPresets[key]);
    },
    [onBoundsChange],
  );

  const presetCopy: Record<
    (typeof PRESET_KEYS)[number],
    { title: string; description: string }
  > = {
    nl: { title: strings.presetNl, description: strings.presetNlDescription },
    bes: { title: strings.presetBes, description: strings.presetBesDescription },
  };

  const selectedStatus: StatusLabel = {
    text: strings.selectedStatusText,
    icon: "check",
  };

  return (
    <div className={panelStyles.tabContentWrapper}>
      <div className={panelStyles.tabHeader}>
        <h2 className={panelStyles.tabTitle}>{strings.title}</h2>
        <p className={panelStyles.tabSubtitle}>{strings.subtitle}</p>
      </div>
      <div className={panelStyles.section}>
        <h3 className={panelStyles.sectionTitleCoord}>{strings.manualTitle}</h3>
        <div className={panelStyles.coordGrid}>
          <CoordinateField
            className={panelStyles.coordNorth}
            label={strings.northLatitude}
            value={bounds.north_latitude}
            min={-90}
            max={90}
            onCommit={(v) => onCoordChange("north_latitude", v)}
          />
          <CoordinateField
            className={panelStyles.coordWest}
            label={strings.westLongitude}
            value={bounds.west_longitude}
            min={-180}
            max={180}
            onCommit={(v) => onCoordChange("west_longitude", v)}
          />
          <CoordinateField
            className={panelStyles.coordEast}
            label={strings.eastLongitude}
            value={bounds.east_longitude}
            min={-180}
            max={180}
            onCommit={(v) => onCoordChange("east_longitude", v)}
          />
          <CoordinateField
            className={panelStyles.coordSouth}
            label={strings.southLatitude}
            value={bounds.south_latitude}
            min={-90}
            max={90}
            onCommit={(v) => onCoordChange("south_latitude", v)}
          />
        </div>
      </div>
      <div className={panelStyles.section}>
        <h3 className={panelStyles.sectionTitle}>{strings.presetsTitle}</h3>
        <div className={panelStyles.presetGrid}>
          {PRESET_KEYS.map((key) => (
            <CheckCard
              key={key}
              title={presetCopy[key].title}
              checked={selectedPreset === key}
              statusLabel={selectedPreset === key ? selectedStatus : undefined}
              onClick={() => handlePresetSelect(key)}
              className={panelStyles.presetCard}
            >
              {presetCopy[key].description}
            </CheckCard>
          ))}
        </div>
      </div>
      <div className={panelStyles.boundsPreviewInTab}>
        <BoundsPreviewMap
          bounds={bounds}
          fitBoundsPadding={40}
          baseLayerStyle={baseLayerStyle}
          onBoundsChange={onBoundsChange}
          boundsInteractionMode="editable"
        />
      </div>
    </div>
  );
}
